import Vue from 'vue';
import patientStoryService from '../../api/patient-story-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '../../util/array';
import { mapErrorsToInputs, scrollToFirstError } from '../../util/forms';
import imageService from '../../api/image-service';

const state = {
  patientStory: null,
  patientStoryPagination: {},
  editedPatientStory: {},
  patientStoryValidationErrors: {},
  patientStoryFilterParams: {},
  loadingPatientStory: false,
  savingPatientStory: false,
  savingImages: false,
};

const mutations = {
  SET_PATIENT_STORY(state, { data, current_page, per_page, total }) {
    state.patientStory = data;
    state.patientStoryPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.patientStoryFilterParams = params;
  },

  STORE_PATIENT_STORY(state, story) {
    state.patientStory = [...state.patientStory, story];
    state.patientStoryValidationErrors = {};
    state.patientStoryPagination.total += 1;
  },

  SET_EDITED_PATIENT_STORY(state, story) {
    state.patientStoryValidationErrors = {};
    state.editedPatientStory = JSON.parse(JSON.stringify(story));
  },

  UPDATE_PATIENT_STORY(state, story) {
    state.patientStory = updateArrayItem(state.patientStory, story);
  },

  DELETE_PATIENT_STORY(state, story) {
    state.patientStory = removeArrayItem(state.patientStory, story);
    state.patientStoryPagination.total -= 1;
  },

  SET_PATIENT_STORY_VALIDATION_ERRORS(state, validationErrors) {
    state.patientStoryValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.patientStoryValidationErrors[field];
  },

  SET_LOADING_PATIENT_STORY(state, loading) {
    state.loadingPatientStory = loading;
  },

  SET_SAVING_PATIENT_STORY(state, saving) {
    state.savingPatientStory = saving;
  },

  SET_SAVING_IMAGES(state, saving) {
    state.savingImages = saving;
  },

  SET_PATIENT_STORY_IMAGE(state, image) {
    Vue.set(state.editedPatientStory, 'image', image);
    for (let i = 0; i < state.patientStory?.length; i++) {
      if (state.patientStory[i].id === +image.model_id) {
        Vue.set(state.patientStory[i], 'image', image);
        break;
      }
    }
  },

  DELETE_PATIENT_STORY_IMAGE(state, image) {
    Vue.set(state.editedPatientStory, 'image', null);
    for (let i = 0; i < state.patientStory?.length; i++) {
      if (state.patientStory[i].id === +image.model_id) {
        Vue.set(state.patientStory[i], 'image', null);
        break;
      }
    }
  },
};

const actions = {
  fetchPatientStory({ commit }, params) {
    commit('SET_LOADING_PATIENT_STORY', true);
    return patientStoryService
      .getPage(params)
      .then((res) => {
        commit('SET_PATIENT_STORY', res.data);
        if (params.page !== state.patientStoryFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_PATIENT_STORY', false);
      });
  },

  async storePatientStory({ commit }) {
    commit('SET_SAVING_PATIENT_STORY', true);
    const res = await patientStoryService.create();
    commit('SET_SAVING_PATIENT_STORY', false);
    commit('STORE_PATIENT_STORY', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('patient_story_created'));
    return res;
  },

  editPatientStory({ state, commit }, storyId) {
    const patientStory = state.patientStory?.find((s) => s.id === storyId);
    if (patientStory) {
      commit('SET_EDITED_PATIENT_STORY', patientStory);
      return Promise.resolve(patientStory);
    }
    return patientStoryService.getById(storyId).then((res) => {
      commit('SET_EDITED_PATIENT_STORY', res.data);
    });
  },

  updatePatientStory({ commit }, story) {
    commit('SET_SAVING_PATIENT_STORY', true);
    return patientStoryService
      .update(story)
      .then((res) => {
        commit('UPDATE_PATIENT_STORY', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('patient_story_updated'));
      })
      .catch((err) => {
        commit('SET_PATIENT_STORY_VALIDATION_ERRORS', mapErrorsToInputs(err));
        scrollToFirstError();
        throw err;
      })
      .finally(() => commit('SET_SAVING_PATIENT_STORY', false));
  },

  deletePatientStory({ commit }, story) {
    openConfirmDialog({
      title: i18n.t('confirm_patient_story_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      patientStoryService.delete(story).then(() => {
        commit('DELETE_PATIENT_STORY', story);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('patient_story_deleted'));
      });
    });
  },

  async uploadPatientStoryImages({ commit }, { images, type, modelType, modelId }) {
    commit('SET_SAVING_IMAGES', true);
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    const res = await imageService.upload(images, type, modelType, modelId);
    commit('SET_SAVING_IMAGES', false);
    commit('SET_PATIENT_STORY_IMAGE', res.data[0]);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_uploaded'));
    return res;
  },

  deletePatientStoryImages({ commit }, image) {
    openConfirmDialog({
      title: i18n.t('confirm_patient_story_image_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(image).then(() => {
        commit('DELETE_PATIENT_STORY_IMAGE', image);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_deleted'));
      });
    });
  },

  clearPatientStoryValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
